/**
 * A part's ports, and adding one (issue #52).
 *
 * A port is where something crosses a part's boundary, so it is declared INSIDE
 * the part def - `port tlmOut : TelemetryPort;` - and every usage of the part
 * has it. Until #52 the server could only file a new port in a package, where it
 * was on no part at all, so this pane listed ports it had no way to add.
 *
 * The type is picked from the program's PORT defs only: a port typed by a
 * message or an interface def parses and means nothing. Conjugation is a
 * checkbox because it is one character on the type - `~TelemetryPort` - and it
 * is the whole difference between the two sides of an interface.
 */

import { useEffect, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { InterfaceFeature, PartDetail, Pinout } from '../../schema'
import { useSchema } from '../../state/AppData'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import ElementPicker from '../common/ElementPicker'
import HelpTip from '../common/HelpTip'
import { portType, sideSummary } from './interfaces-model'

export const PORTS_HELP =
  'Where something crosses this part’s boundary. A port is declared inside the ' +
  'part definition, so every usage of it has the port. Its type is a port definition, ' +
  'which says what flows in and out; a conjugated port (~) has every direction reversed, ' +
  'which is how the two sides of one interface are stated once.'

export const NO_PORTS = 'No Ports'

export default function PortsSection({
  part,
  refusal,
  onChanged,
}: {
  part: PartDetail
  /** Why a port cannot be added here, or '' when it can. */
  refusal: string
  onChanged: () => void
}) {
  const [adding, setAdding] = useState(false)
  const ports = part.ports ?? []

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Typography variant="subtitle2">Ports</Typography>
          <HelpTip label="What a port is" title={PORTS_HELP} />
        </Box>
        <Tooltip title={refusal} describeChild>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={Boolean(refusal)}
              onClick={() => setAdding(true)}
            >
              Add port
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {ports.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, borderStyle: 'dashed', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {part.is_definition
              ? NO_PORTS
              : 'Nothing of its own — its ports are declared on the definition it is typed by.'}
          </Typography>
        </Paper>
      ) : (
        <Table size="small" aria-label="Ports on this part">
          <TableHead>
            <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableCell>Port</TableCell>
              <TableCell>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ports.map((port) => (
              <TableRow key={port.qualified_name}>
                <TableCell>
                  {port.name}
                  {port.id && port.id !== port.name && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontFamily: MONOSPACE }}
                    >
                      {port.id}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ fontFamily: MONOSPACE }}>
                  {portType(port)}
                  {port.multiplicity}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {adding && (
        <AddPortDialog
          part={part}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false)
            onChanged()
          }}
        />
      )}
    </>
  )
}


/**
 * Declare one port inside a part definition.
 *
 * The SIDE is asked in words rather than as a "conjugated" checkbox: which of
 * the two sides the port def describes is this part - the one that sends what
 * the port def sends, or the mating one that receives it. The `~` follows from
 * the answer. A connector's pin count is shown, because choosing an RJ45 is
 * choosing its eight pins.
 */
export function AddPortDialog({
  part,
  onClose,
  onAdded,
}: {
  part: PartDetail
  onClose: () => void
  onAdded: () => void
}) {
  const schema = useSchema()
  const toasts = useToasts()
  const [name, setName] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [typeName, setTypeName] = useState('')
  const [conjugate, setConjugate] = useState(false)
  const [features, setFeatures] = useState<InterfaceFeature[]>([])
  const [pinout, setPinout] = useState<Pinout | null>(null)

  useEffect(() => {
    if (!typeName) {
      setFeatures([])
      setPinout(null)
      return undefined
    }
    let live = true
    api.interfaceFeatures(typeName)
      .then((found) => live && setFeatures(found))
      .catch(() => live && setFeatures([]))
    api.pinout(typeName)
      .then((found) => live && setPinout(found))
      .catch(() => live && setPinout(null))
    return () => {
      live = false
    }
  }, [typeName])

  const asDeclared = sideSummary(features, false)
  const mating = sideSummary(features, true)
  const [statement, setStatement] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const submit = async () => {
    setBusy(true)
    setFailure('')
    setFieldErrors({})
    try {
      const body: Record<string, unknown> = {
        package: part.qualified_name,
        identifier,
        name,
      }
      if (typeName) body.type_name = typeName
      if (typeName && conjugate) body.is_conjugate = true
      if (statement) body.statement = statement
      const created = await api.create('ports', body)
      toasts.succeeded(`${created.identifier || name} added to ${part.name || part.id}`)
      onAdded()
    } catch (error) {
      const failed = error as ApiError
      setFieldErrors(failed.fields ?? {})
      if (!Object.keys(failed.fields ?? {}).length) setFailure(failed.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a port to {part.name || part.id}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}
          <TextField
            label="Name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name || 'How an interface names it: tlmOut, or a reference designator like J1 for a connector'}
            size="small"
            slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
          />
          <TextField
            label="ID"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            error={Boolean(fieldErrors.identifier)}
            helperText={fieldErrors.identifier || 'Optional short name, e.g. PRT_TLM_1'}
            size="small"
            slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
          />
          <ElementPicker
            schema={schema}
            kind="interface"
            // Port defs only: a port typed by a message or an interface def
            // parses and means nothing.
            where={(row) => row.declared_as === 'port'}
            valueFor={(row) => String(row.qualified_name ?? row.id)}
            value={typeName || null}
            onChange={setTypeName}
            label="Port definition"
            error={Boolean(fieldErrors.type_name)}
            helperText={fieldErrors.type_name || 'What flows through it, and which way'}
          />
          {pinout && pinout.pins.length > 0 && (
            <Alert severity="info" icon={false}>
              A connector{pinout.connector_type ? ` (${pinout.connector_type})` : ''} with{' '}
              {pinout.pins.length} pins
              {pinout.pins.some((pin) => pin.function)
                ? `, ${pinout.pins.filter((pin) => pin.function).length} of them assigned`
                : ''}
              . Its pinout comes with it.
            </Alert>
          )}
          {typeName && (
            <RadioGroup
              value={conjugate ? 'mating' : 'declared'}
              onChange={(event) => setConjugate(event.target.value === 'mating')}
              aria-label="Which side this part is"
            >
              <FormControlLabel
                value="declared"
                control={<Radio size="small" />}
                label={`This part ${asDeclared || 'is the side the port definition describes'}`}
              />
              <FormControlLabel
                value="mating"
                control={<Radio size="small" />}
                label={`This part is the mating side (~)${mating ? `: it ${mating}` : ''}`}
              />
            </RadioGroup>
          )}
          <TextField
            label="Description"
            value={statement}
            onChange={(event) => setStatement(event.target.value)}
            multiline
            minRows={2}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={!name.trim() || busy}
        >
          {busy ? 'Adding…' : 'Add port'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
